import React, { useState, useEffect } from 'react';
import { Trophy, Gamepad2, FileText, Users, Plus, Save, Mail, Lock, User, LogOut } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const GamingPortfolio = () => {
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  const saveCache = (key, data) => {
    try {
      localStorage.setItem(`cache_${key}`, JSON.stringify({ ts: Date.now(), data }));
    } catch (e) {
      // Ignore cache write errors
    }
  };
  const loadCache = (key, maxAge = CACHE_TTL) => {
    try {
      const raw = localStorage.getItem(`cache_${key}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.ts) return null;
      if (Date.now() - parsed.ts > maxAge) {
        localStorage.removeItem(`cache_${key}`);
        return null;
      }
      return parsed.data;
    } catch (e) {
      return null;
    }
  };

  // Initialize auth and cached states synchronously so UI shows immediately on refresh
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const u = localStorage.getItem('user');
      return u ? JSON.parse(u) : null;
    } catch (e) { return null; }
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('token'));
  const [authMode, setAuthMode] = useState('login');
  const [formData, setFormData] = useState({
    email: '', password: '', username: '', confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [activeTab, setActiveTab] = useState('dashboard');
  const [games, setGames] = useState(() => loadCache('games') || []);
  const [socials, setSocials] = useState(() => loadCache('socials') || []);
  const [documents, setDocuments] = useState(() => loadCache('documents') || []);
  const [editingGame, setEditingGame] = useState(null);
  const [newGame, setNewGame] = useState({
    name: '', inGameName: '', gameId: '', platform: '', status: 'Playing', logo: '', photos: []
  });
  // Social media & documents form state
  const [newSocial, setNewSocial] = useState({ name: '', url: '', email: '', phone: '', logo: '', photos: [] });
  const [editingSocial, setEditingSocial] = useState(null);
  const [newDocument, setNewDocument] = useState({ name: '', photo: '' });
  const [editingDocument, setEditingDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [lightboxImage, setLightboxImage] = useState(null);

  // close lightbox on ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setLightboxImage(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Persist caches when state changes so refresh shows recent data immediately
  useEffect(() => { saveCache('games', games); }, [games]);
  useEffect(() => { saveCache('socials', socials); }, [socials]);
  useEffect(() => { saveCache('documents', documents); }, [documents]);

  useEffect(() => {
    // If a token is present (from initializer or login), ensure we have currentUser and fetch fresh data
    if (token) {
      if (!currentUser) {
        try {
          const savedUser = localStorage.getItem('user');
          if (savedUser) setCurrentUser(JSON.parse(savedUser));
        } catch (e) { /* ignore */ }
      }
      setIsAuthenticated(true);
      loadAllData(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadAllData = async (authToken) => {
    try {
      const headers = { 'Authorization': `Bearer ${authToken}` };

      // Start all requests in parallel and update state as each resolves so UI fills progressively
      fetch(`${API_URL}/games`, { headers })
        .then(async (res) => { if (res.ok) { const data = await res.json(); setGames(data); saveCache('games', data); } })
        .catch(err => console.error('Error fetching games:', err));

      fetch(`${API_URL}/social`, { headers })
        .then(async (res) => { if (res.ok) { const data = await res.json(); setSocials(data); saveCache('socials', data); } })
        .catch(err => console.error('Error fetching socials:', err));

      fetch(`${API_URL}/documents`, { headers })
        .then(async (res) => { if (res.ok) { const data = await res.json(); setDocuments(data); saveCache('documents', data); } })
        .catch(err => console.error('Error fetching documents:', err));

    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Invalid email format';
    }
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    if (authMode === 'signup') {
      if (!formData.username) {
        errors.username = 'Username is required';
      }
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    setMessage('');

    try {
      if (authMode === 'signup') {
        const response = await fetch(`${API_URL}/auth/signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: formData.username,
            email: formData.email,
            password: formData.password
          })
        });

        const data = await response.json();

        if (response.ok) {
          setMessage(data.message);
          setTimeout(() => {
            setAuthMode('login');
            setFormData({ email: '', password: '', username: '', confirmPassword: '' });
            setMessage('');
          }, 3000);
        } else {
          setFormErrors({ email: data.message });
        }
      } else {
        const response = await fetch(`${API_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password
          })
        });

        const data = await response.json();

        if (response.ok) {
          setToken(data.token);
          setCurrentUser(data.user);
          setIsAuthenticated(true);
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          loadAllData(data.token);
        } else {
          setFormErrors({ password: data.message });
        }
      }
    } catch (error) {
      setFormErrors({ password: 'Server error. Make sure backend is running on localhost:5000' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setGames([]);
  };

  const handleImageUpload = (e, setter, field) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (field) {
          setter(prev => ({ ...prev, [field]: reader.result }));
        } else {
          setter(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // handle multiple image uploads (for photos arrays)
  const handleMultiImageUpload = (e, setter, field) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const readers = files.map(file => new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(file);
    }));
    Promise.all(readers).then(images => {
      if (field) {
        setter(prev => ({ ...prev, [field]: [...(prev[field] || []), ...images] }));
      } else {
        setter(prev => [...(prev || []), ...images]);
      }
    });
  };


  // Add photos to an existing game (editing)
  const addPhotosToExistingGame = (e, game) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const readers = files.map(file => new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(file);
    }));
    Promise.all(readers).then(images => {
      const updatedGame = { ...game, photos: [...(game.photos || []), ...images] };
      updateGame(game._id, updatedGame);
    });
  };

  const removePhotoFromGame = (game, idx) => {
    if (!window.confirm('Remove this photo?')) return;
    const updatedPhotos = (game.photos || []).filter((_, i) => i !== idx);
    const updatedGame = { ...game, photos: updatedPhotos };
    updateGame(game._id, updatedGame);
  };

  const addGame = async () => {
    if (!newGame.name || !newGame.platform) return;
    
    try {
      const response = await fetch(`${API_URL}/games`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newGame)
      });

      if (response.ok) {
        const savedGame = await response.json();
        setGames([...games, savedGame]);
        setNewGame({ name: '', inGameName: '', gameId: '', platform: '', status: 'Playing', logo: '', photos: [] });
      }
    } catch (error) {
      console.error('Error adding game:', error);
    }
  };

const deleteGame = async (id) => {
  if (!window.confirm('Delete this game?')) return;
  try {
    const response = await fetch(`${API_URL}/games?id=${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) {
      setGames(games.filter(g => g._id !== id));
    }
  } catch (error) {
    console.error('Error deleting game:', error);
  }
};

const updateGame = async (id, updated) => {
  if (!window.confirm('Update this game?')) return;
  try {
    const response = await fetch(`${API_URL}/games?id=${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updated)
    });
    if (response.ok) {
      const updatedGame = await response.json();
      setGames(games.map(g => g._id === id ? updatedGame : g));
      setEditingGame(null);
    }
  } catch (error) {
    console.error('Error updating game:', error);
  }
};

// Social media handlers
const addSocial = async () => {
  if (!newSocial.name && !newSocial.url) return;
  try {
    const response = await fetch(`${API_URL}/social`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(newSocial)
    });
    if (response.ok) {
      const saved = await response.json();
      setSocials([saved, ...socials]);
      setNewSocial({ name: '', url: '', email: '', phone: '', logo: '', photos: [] });
    }
  } catch (error) {
    console.error('Error adding social:', error);
  }
};

const updateSocial = async (id, updated) => {
  if (!window.confirm('Update this social link?')) return;
  try {
    const response = await fetch(`${API_URL}/social?id=${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updated)
    });
    if (response.ok) {
      const updatedS = await response.json();
      setSocials(socials.map(s => s._id === id ? updatedS : s));
      setEditingSocial(null);
      setNewSocial({ name: '', url: '', email: '', phone: '', logo: '', photos: [] });
    }
  } catch (error) {
    console.error('Error updating social:', error);
  }
};

const deleteSocial = async (id) => {
  if (!window.confirm('Delete this social link?')) return;
  try {
    const response = await fetch(`${API_URL}/social?id=${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) {
      setSocials(socials.filter(s => s._id !== id));
    }
  } catch (error) {
    console.error('Error deleting social:', error);
  }
};

// Document handlers
const addDocument = async () => {
  if (!newDocument.name) return;
  try {
    const response = await fetch(`${API_URL}/documents`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(newDocument)
    });
    if (response.ok) {
      const saved = await response.json();
      setDocuments([saved, ...documents]);
      setNewDocument({ name: '', photo: '' });
    }
  } catch (error) {
    console.error('Error adding document:', error);
  }
};

const updateDocument = async (id, updated) => {
  if (!window.confirm('Update this document?')) return;
  try {
    const response = await fetch(`${API_URL}/documents?id=${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updated)
    });
    if (response.ok) {
      const updatedD = await response.json();
      setDocuments(documents.map(d => d._id === id ? updatedD : d));
      setEditingDocument(null);
      setNewDocument({ name: '', photo: '' });
    }
  } catch (error) {
    console.error('Error updating document:', error);
  }
};

const deleteDocument = async (id) => {
  if (!window.confirm('Delete this document?')) return;
  try {
    const response = await fetch(`${API_URL}/documents?id=${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) {
      setDocuments(documents.filter(d => d._id !== id));
    }
  } catch (error) {
    console.error('Error deleting document:', error);
  }
};

  const playingGames = games.filter(g => g.status === 'Playing').length;

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center px-4">
        <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-8 border border-purple-500/20 w-full max-w-md">
          <div className="text-center mb-8">
            <FileText className="text-purple-400 mx-auto mb-4" size={64} />
            <h1 className="text-3xl font-bold text-white mb-2">Record Kotha</h1>
            <p className="text-purple-300">Records haru ko ghar </p>
          </div>
          <div className="flex gap-2 mb-6">
            <button onClick={() => { setAuthMode('login'); setFormErrors({}); setMessage(''); }} className={`flex-1 py-3 rounded-lg font-semibold transition-all ${authMode === 'login' ? 'bg-purple-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>Login</button>
            <button onClick={() => { setAuthMode('signup'); setFormErrors({}); setMessage(''); }} className={`flex-1 py-3 rounded-lg font-semibold transition-all ${authMode === 'signup' ? 'bg-purple-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>Sign Up</button>
          </div>

          {message && (
            <div className="mb-4 p-4 bg-green-900/30 border border-green-500/30 rounded-lg">
              <p className="text-green-300 text-sm">{message}</p>
            </div>
          )}

          <form onSubmit={handleAuth} className="space-y-4">
            {authMode === 'signup' && (
              <div>
                <label className="block text-purple-300 mb-2 text-sm">Username</label>
                <div className="relative">
                  <User className="absolute left-3 top-3 text-slate-400" size={20} />
                  <input type="text" value={formData.username} onChange={(e) => setFormData({...formData, username: e.target.value})} className="w-full bg-slate-700 text-white pl-10 pr-4 py-3 rounded-lg border border-slate-600 focus:border-purple-500 focus:outline-none" placeholder="Enter username" />
                </div>
                {formErrors.username && <p className="text-red-400 text-sm mt-1">{formErrors.username}</p>}
              </div>
            )}
            <div>
              <label className="block text-purple-300 mb-2 text-sm">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 text-slate-400" size={20} />
                <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full bg-slate-700 text-white pl-10 pr-4 py-3 rounded-lg border border-slate-600 focus:border-purple-500 focus:outline-none" placeholder="Enter email" />
              </div>
              {formErrors.email && <p className="text-red-400 text-sm mt-1">{formErrors.email}</p>}
            </div>
            <div>
              <label className="block text-purple-300 mb-2 text-sm">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 text-slate-400" size={20} />
                <input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} className="w-full bg-slate-700 text-white pl-10 pr-4 py-3 rounded-lg border border-slate-600 focus:border-purple-500 focus:outline-none" placeholder="Enter password" />
              </div>
              {formErrors.password && <p className="text-red-400 text-sm mt-1">{formErrors.password}</p>}
            </div>
            {authMode === 'signup' && (
              <div>
                <label className="block text-purple-300 mb-2 text-sm">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 text-slate-400" size={20} />
                  <input type="password" value={formData.confirmPassword} onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})} className="w-full bg-slate-700 text-white pl-10 pr-4 py-3 rounded-lg border border-slate-600 focus:border-purple-500 focus:outline-none" placeholder="Confirm password" />
                </div>
                {formErrors.confirmPassword && <p className="text-red-400 text-sm mt-1">{formErrors.confirmPassword}</p>}
              </div>
            )}
            <button type="submit" disabled={loading} className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50">
              {loading ? 'Processing...' : (authMode === 'login' ? 'Login' : 'Create Account')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex flex-col">
      <div className="flex-grow container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-between mb-4">
            <div></div>
            <h1 className="text-5xl font-bold text-white flex items-center gap-3">
              <FileText className="text-purple-400" size={48} />
              Record Kotha
            </h1>
            <button onClick={handleLogout} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
              <LogOut size={18} /> Logout
            </button>
          </div>
          <p className="text-purple-300">Welcome, {currentUser?.username}</p>
        </div>

        <div className="flex justify-center gap-4 mb-8">
          {['dashboard', 'games', 'social', 'documents'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} className={`px-6 py-3 rounded-lg font-semibold transition-all ${activeTab === tab ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/50' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}>
              {tab === 'dashboard' && <Trophy className="inline mr-2" size={20} />}
              {tab === 'games' && <Gamepad2 className="inline mr-2" size={20} />}
              {tab === 'social' && <Users className="inline mr-2" size={20} />}
              {tab === 'documents' && <Mail className="inline mr-2" size={20} />}
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/20">
                <h3 className="text-purple-400 text-lg mb-2">Total Games</h3>
                <p className="text-4xl font-bold text-white">{games.length}</p>
              </div>
              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/20">
                <h3 className="text-purple-400 text-lg mb-2">Playing Games</h3>
                <p className="text-4xl font-bold text-white">{playingGames}</p>
              </div>
              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/20">
                <h3 className="text-purple-400 text-lg mb-2">Total Social Medias</h3>
                <p className="text-4xl font-bold text-white">{socials.length}</p>
              </div>
              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/20">
                <h3 className="text-purple-400 text-lg mb-2">Total Documents</h3>
                <p className="text-4xl font-bold text-white">{documents.length}</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Games Section */}
              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/20">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                  <Gamepad2 size={28} className="text-purple-400" />
                  Games
                </h2>
                {games.length === 0 ? (
                  <p className="text-slate-400 text-center py-8">No games added yet!</p>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {games.map(game => (
                      <div key={game._id} className="bg-slate-700/50 rounded-lg p-4 border border-purple-500/20 hover:border-purple-500/50 transition-all">
                          <div className="flex items-start gap-4">
                          {(game.logo) ? (
                            <button onClick={() => setLightboxImage(game.logo)} className="w-16 h-16 overflow-hidden rounded-lg flex-shrink-0 focus:outline-none relative">
                              <img src={game.logo} alt={game.name} className="w-full h-full object-cover" />
                              <span className={`absolute -top-2 -right-2 px-2 py-1 rounded-full text-xs ${game.status === 'Not Playing' ? 'bg-red-600' : game.status === 'Playing' ? 'bg-blue-600' : 'bg-slate-600'} text-white`}>{game.status}</span>
                            </button>
                          ) : (
                            <div className="w-16 h-16 bg-slate-600 rounded-lg flex items-center justify-center flex-shrink-0 relative">
                              <Gamepad2 className="text-slate-400" size={24} />
                              <span className={`absolute -top-2 -right-2 px-2 py-1 rounded-full text-xs ${game.status === 'Not Playing' ? 'bg-red-600' : game.status === 'Playing' ? 'bg-blue-600' : 'bg-slate-600'} text-white`}>{game.status}</span>
                            </div>
                          )}

                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-white mb-1">{game.name} {game.platform && <span className="text-purple-400 text-sm">{game.platform}</span>}</h3>
                            <p className="text-slate-400 text-sm mb-2">{game.inGameName} {game.gameId ? <span className="text-xs text-slate-300 ml-2">ID: {game.gameId}</span> : null}</p>
                            {game.photos && game.photos.length > 0 && (
                              <div className="mt-2 flex gap-2">
                                {game.photos.slice(0,3).map((p, idx) => (
                                  <button key={idx} onClick={() => setLightboxImage(p)} className="w-20 h-20 overflow-hidden rounded-lg focus:outline-none">
                                    <img src={p} alt={`photo-${idx}`} className="w-full h-full object-cover" />
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Social Media Section */}
              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/20">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                  <Users size={28} className="text-purple-400" />
                  Social Media
                </h2>
                {socials.length === 0 ? (
                  <p className="text-slate-400 text-center py-6">No social links yet!</p>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {socials.map(s => (
                      <div key={s._id || s.url} className="bg-slate-700/50 p-4 rounded-lg border border-purple-500/10 relative">
                        { (s.logo || (s.photos && s.photos.length > 0)) ? (
                          <button onClick={() => setLightboxImage(s.logo || s.photos[0])} className="absolute top-3 left-3 w-16 h-16 overflow-hidden rounded-lg focus:outline-none shadow-lg">
                            <img src={s.logo || s.photos[0]} alt={s.name} className="w-full h-full object-cover" />
                          </button>
                        ) : (
                          <div className="absolute top-3 left-3 w-16 h-16 bg-slate-600 rounded-lg flex items-center justify-center">
                            <Mail className="text-slate-300" size={20} />
                          </div>
                        )}

                        <div className="pl-20">
                          <p className="text-white font-semibold">{s.name || s.email || s.phone}</p>
                          {s.email && <p className="text-slate-400 text-sm">{s.email}</p>}
                          {s.phone && <p className="text-slate-400 text-sm">{s.phone}</p>}
                          {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="text-purple-300 text-sm break-all">{s.url}</a>}
                          {s.photos && s.photos.length > 0 && (
                            <div className="mt-2 flex gap-2">
                              {s.photos.slice(0,3).map((p, idx) => (
                                <button key={idx} onClick={() => setLightboxImage(p)} className="w-20 h-20 overflow-hidden rounded-lg focus:outline-none">
                                  <img src={p} alt={`photo-${idx}`} className="w-full h-full object-cover" />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Documents Section */}
              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/20">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                  <Mail size={20} className="text-purple-400" />
                  Documents
                </h2>
                {documents.length === 0 ? (
                  <p className="text-slate-400 text-center py-6">No documents uploaded yet!</p>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {documents.map(d => (
                      <div key={d._id} className="bg-slate-700/50 p-4 rounded-lg border border-purple-500/10">
                        {d.photo ? (
                          <button onClick={() => setLightboxImage(d.photo)} className="w-full h-36 overflow-hidden rounded-lg focus:outline-none">
                            <img src={d.photo} alt={d.name} className="w-full h-full object-cover" />
                          </button>
                        ) : (
                          <div className="w-full h-36 bg-slate-600 rounded-lg flex items-center justify-center">—</div>
                        )}
                        <div className="mt-3">
                          <p className="text-white font-semibold">{d.name}</p>
                          {/* <p className="text-slate-400 text-sm">{d.uploadedAt ? new Date(d.uploadedAt).toLocaleDateString() : ''}</p> */}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'games' && (
          <div className="space-y-6">
            <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/20">
              <h2 className="text-2xl font-bold text-white mb-4">Add New Game</h2>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <input type="text" placeholder="Game Name" value={newGame.name} onChange={(e) => setNewGame({...newGame, name: e.target.value})} className="bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600 focus:border-purple-500 focus:outline-none" />
                <input type="text" placeholder="In Game Name (IGN)" value={newGame.inGameName} onChange={(e) => setNewGame({...newGame, inGameName: e.target.value})} className="bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600 focus:border-purple-500 focus:outline-none" />
                <input type="text" placeholder="Game ID" value={newGame.gameId} onChange={(e) => setNewGame({...newGame, gameId: e.target.value})} className="bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600 focus:border-purple-500 focus:outline-none" />
                <input type="text" placeholder="Platform" value={newGame.platform} onChange={(e) => setNewGame({...newGame, platform: e.target.value})} className="bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600 focus:border-purple-500 focus:outline-none" />
                <select value={newGame.status} onChange={(e) => setNewGame({...newGame, status: e.target.value})} className="bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600 focus:border-purple-500 focus:outline-none">
                  <option>Playing</option>
                  <option>Not Playing</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-purple-300 mb-2">Game Logo</label>
                <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, (img) => setNewGame({...newGame, logo: img}))} className="bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600 w-full" />
              </div>
              <div className="mb-4">
                <label className="block text-purple-300 mb-2">Photos</label>
                <input type="file" accept="image/*" multiple onChange={(e) => handleMultiImageUpload(e, setNewGame, 'photos')} className="bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600 w-full" />
                {newGame.photos && newGame.photos.length > 0 && (
                  <div className="mt-2 flex gap-2 overflow-x-auto">
                    {newGame.photos.map((p, idx) => (
                      <img key={idx} src={p} alt={`photo-${idx}`} className="w-20 h-20 object-cover rounded-lg" />
                    ))}
                  </div>
                )}
              </div>
              <button onClick={addGame} className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors w-full justify-center">
                <Plus size={20} /> Add new game
              </button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {games.map(game => (
                <div key={game._id} className="bg-slate-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/20">
                  {editingGame === game._id ? (
                    <div className="space-y-3">
                      <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, (img) => updateGame(game._id, { ...game, logo: img }))} className="bg-slate-700 text-white px-3 py-2 rounded-lg border border-slate-600 w-full text-sm" />
                      <input type="text" value={game.name} onChange={(e) => {
                        const updated = games.map(g => g._id === game._id ? {...g, name: e.target.value} : g);
                        setGames(updated);
                      }} className="w-full bg-slate-700 text-white px-3 py-2 rounded-lg border border-slate-600" />
                      <input type="text" value={game.inGameName || ''} onChange={(e) => {
                        const updated = games.map(g => g._id === game._id ? {...g, inGameName: e.target.value} : g);
                        setGames(updated);
                      }} className="w-full bg-slate-700 text-white px-3 py-2 rounded-lg border border-slate-600" placeholder="In-Game Name (IGN)" />
                      <input type="text" value={game.gameId || ''} onChange={(e) => {
                        const updated = games.map(g => g._id === game._id ? {...g, gameId: e.target.value} : g);
                        setGames(updated);
                      }} className="w-full bg-slate-700 text-white px-3 py-2 rounded-lg border border-slate-600" placeholder="Game ID" />
                      <input type="text" value={game.platform} onChange={(e) => {
                        const updated = games.map(g => g._id === game._id ? {...g, platform: e.target.value} : g);
                        setGames(updated);
                      }} className="w-full bg-slate-700 text-white px-3 py-2 rounded-lg border border-slate-600" />
                      <div>
                        <label className="block text-purple-300 mb-2">Photos</label>
                        <input type="file" accept="image/*" multiple onChange={(e) => addPhotosToExistingGame(e, game)} className="bg-slate-700 text-white px-3 py-2 rounded-lg border border-slate-600 w-full text-sm" />
                        {game.photos && game.photos.length > 0 && (
                          <div className="mt-2 grid grid-cols-3 gap-2">
                            {game.photos.map((p, idx) => (
                              <div key={idx} className="relative">
                                <img src={p} alt={`photo-${idx}`} className="w-full h-20 object-cover rounded-lg" />
                                <button onClick={() => removePhotoFromGame(game, idx)} className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 text-xs">Remove</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <select value={game.status} onChange={(e) => {
                        const updated = games.map(g => g._id === game._id ? {...g, status: e.target.value} : g);
                        setGames(updated);
                      }} className="w-full bg-slate-700 text-white px-3 py-2 rounded-lg border border-slate-600">
                        <option>Playing</option>
                        <option>Not Playing</option>
                      </select>
                      <button onClick={() => updateGame(game._id, game)} className="w-full bg-purple-600 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2">
                        <Save size={16} /> Update Game
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start gap-4">
                        {(game.logo) ? (
                          <button onClick={() => setLightboxImage(game.logo)} className="w-16 h-16 overflow-hidden rounded-lg flex-shrink-0 focus:outline-none">
                            <img src={game.logo} alt={game.name} className="w-full h-full object-cover" />
                          </button>
                        ) : (
                          <div className="w-16 h-16 bg-slate-700 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Gamepad2 className="text-slate-500" size={24} />
                          </div>
                        )}

                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-white mb-2">
                            {game.name} {game.platform ? <span className="text-sm text-slate-400">({game.platform})</span> : null}
                          </h3>
                          <p className="text-purple-400 mb-1">
                            {game.inGameName} {game.gameId ? <span className="text-xs text-slate-300 ml-2">ID: {game.gameId}</span> : null}
                          </p>
                          {game.photos && game.photos.length > 0 && (
                            <div className="mt-2 flex gap-2 mb-2">
                              {game.photos.map((p, idx) => (
                                <button key={idx} onClick={() => setLightboxImage(p)} className="w-20 h-20 overflow-hidden rounded-lg focus:outline-none">
                                  <img src={p} alt={`photo-${idx}`} className="w-full h-full object-cover cursor-pointer" />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-slate-400 mb-2">{game.createdAt ? new Date(game.createdAt).toLocaleDateString() : '—'}</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm ${game.status === 'Not Playing' ? 'bg-red-600' : game.status === 'Playing' ? 'bg-blue-600' : 'bg-slate-600'} text-white mb-4`}>{game.status}</span>
                      <div className="flex gap-2">
                        <button onClick={() => setEditingGame(game._id)} className="bg-slate-700 hover:bg-slate-600 text-white px-3 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 transition-colors">
                          Edit
                        </button>
                        <button onClick={() => deleteGame(game._id)} className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg transition-colors">
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'social' && (
          <div className="space-y-6">
            <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/20">
              <h2 className="text-2xl font-bold text-white mb-4">Social Media</h2>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <input type="text" placeholder="Name" value={newSocial.name} onChange={(e) => setNewSocial({...newSocial, name: e.target.value})} className="bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600" />
                <input type="text" placeholder="URL" value={newSocial.url} onChange={(e) => setNewSocial({...newSocial, url: e.target.value})} className="bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600" />
                <input type="email" placeholder="Email" value={newSocial.email} onChange={(e) => setNewSocial({...newSocial, email: e.target.value})} className="bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600" />
                <input type="text" placeholder="Phone" value={newSocial.phone} onChange={(e) => setNewSocial({...newSocial, phone: e.target.value})} className="bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600" />
                <div className="md:col-span-2 grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-purple-300 mb-2">Logo</label>
                    <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, setNewSocial, 'logo')} className="w-full bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600" />
                  </div>
                  <div>
                    <label className="block text-purple-300 mb-2">Additional Photos</label>
                    <input type="file" accept="image/*" multiple onChange={(e) => handleMultiImageUpload(e, setNewSocial, 'photos')} className="w-full bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600" />
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { editingSocial ? updateSocial(editingSocial, newSocial) : addSocial(); }} className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg">{editingSocial ? 'Save Social' : 'Add Social'}</button>
                {editingSocial && <button onClick={() => { setEditingSocial(null); setNewSocial({ name: '', url: '', email: '', phone: '', logo: '', photos: [] }); }} className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg">Cancel</button>}
              </div>

              <div className="grid md:grid-cols-2 gap-4 mt-4">
                {socials.map(s => (
                  <div key={s._id} className="bg-slate-700/50 p-4 rounded-lg border border-purple-500/10 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {(s.logo || (s.photos && s.photos.length > 0)) ? <img src={s.logo || s.photos[0]} alt={s.name} className="w-12 h-12 object-cover rounded-lg" /> : <div className="w-12 h-12 bg-slate-600 rounded-lg flex items-center justify-center"><Mail className="text-slate-300" size={18} /></div>}
                      <div>
                        <p className="text-white font-semibold">{s.name}</p>
                        {s.email && <p className="text-slate-400 text-sm">{s.email}</p>}
                        {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="text-purple-300 text-sm break-all">{s.url}</a>}
                        {s.photos && s.photos.length > 0 && (
                          <div className="mt-2 flex gap-2">
                            {s.photos.slice(0,3).map((p, idx) => (
                              <button key={idx} onClick={() => setLightboxImage(p)} className="w-12 h-12 overflow-hidden rounded-lg focus:outline-none">
                                <img src={p} alt={`photo-${idx}`} className="w-full h-full object-cover" />
                              </button>
                            ))}
                            {s.createdAt && <p className="text-xs text-slate-400">{new Date(s.createdAt).toLocaleDateString()}</p>}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => { setEditingSocial(s._id); setNewSocial({ name: s.name || '', url: s.url || '', email: s.email || '', phone: s.phone || '', logo: s.logo || '', photos: s.photos || [] }); }} className="bg-slate-700 hover:bg-slate-600 text-white px-3 py-1 rounded-lg">Edit</button>
                      <button onClick={() => deleteSocial(s._id)} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-lg">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="space-y-6">
            <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/20">
              <h2 className="text-2xl font-bold text-white mb-4">Documents</h2>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <input type="text" placeholder="Document Name" value={newDocument.name} onChange={(e) => setNewDocument({...newDocument, name: e.target.value})} className="bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600" />
                <div>
                  <label className="block text-purple-300 mb-2">File (image)</label>
                  <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, setNewDocument, 'photo')} className="w-full bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600" />
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { editingDocument ? updateDocument(editingDocument, newDocument) : addDocument(); }} className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg">{editingDocument ? 'Save Document' : 'Add Document'}</button>
                {editingDocument && <button onClick={() => { setEditingDocument(null); setNewDocument({ name: '', photo: '' }); }} className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg">Cancel</button>}
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                {documents.map(d => (
                  <div key={d._id} className="bg-slate-700/50 p-4 rounded-lg border border-purple-500/10">
                    {d.photo ? (
                      <button onClick={() => setLightboxImage(d.photo)} className="w-full h-40 overflow-hidden rounded-lg focus:outline-none">
                        <img src={d.photo} alt={d.name} className="w-full h-full object-cover" />
                      </button>
                    ) : (
                      <div className="w-full h-40 bg-slate-600 rounded-lg flex items-center justify-center">—</div>
                    )}
                    <div className="mt-3 flex items-center justify-between">
                      <div>
                        <p className="text-white font-semibold">{d.name}</p>
                        <p className="text-slate-400 text-sm">{d.uploadedAt ? new Date(d.uploadedAt).toLocaleDateString() : ''}</p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button onClick={() => { setEditingDocument(d._id); setNewDocument({ name: d.name || '', photo: d.photo || '' }); }} className="bg-slate-700 hover:bg-slate-600 text-white px-3 py-1 rounded-lg">Edit</button>
                        <button onClick={() => deleteDocument(d._id)} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-lg">Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {lightboxImage && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50" onClick={() => setLightboxImage(null)}>
          <div className="relative max-w-[95vw] max-h-[95vh]">
            <img src={lightboxImage} alt="Full" className="max-h-[90vh] max-w-[90vw] rounded-lg" onClick={(e) => e.stopPropagation()} />
            <button onClick={() => setLightboxImage(null)} className="absolute -top-3 -right-3 bg-red-600 text-white rounded-full p-2">×</button>
          </div>
        </div>
      )}

      {lightboxImage && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50" onClick={() => setLightboxImage(null)}>
          <div className="relative max-w-[95vw] max-h-[95vh]">
            <img src={lightboxImage} alt="Full" className="max-h-[90vh] max-w-[90vw] rounded-lg" onClick={(e) => e.stopPropagation()} />
            <button onClick={() => setLightboxImage(null)} className="absolute -top-3 -right-3 bg-red-600 text-white rounded-full p-2">×</button>
          </div>
        </div>
      )}

      <footer className="bg-slate-900/80 backdrop-blur border-t border-purple-500/20 py-6 mt-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 text-center md:text-left">
            <div>
              <h3 className="text-white font-bold text-lg mb-2 flex items-center justify-center md:justify-start gap-2">
                <FileText size={24} className="text-purple-400" />
                Record Kotha
              </h3>
              <p className="text-slate-400 text-sm">Keep your records safely.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-2">Quick Links</h4>
              <ul className="space-y-1 text-slate-400 text-sm">
                <li><button onClick={() => setActiveTab('dashboard')} className="hover:text-purple-400 transition-colors">Dashboard</button></li>
                <li><button onClick={() => setActiveTab('games')} className="hover:text-purple-400 transition-colors">My Games</button></li>
                <li><button onClick={() => setActiveTab('social')} className="hover:text-purple-400 transition-colors">Social Media</button></li>
                <li><button onClick={() => setActiveTab('documents')} className="hover:text-purple-400 transition-colors">Documents</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-2">Account</h4>
              <p className="text-slate-400 text-sm mb-2">{currentUser?.email}</p>
              <button onClick={handleLogout} className="text-purple-400 hover:text-purple-300 text-sm transition-colors">
                Logout
              </button>
            </div>
          </div>
          <div className="border-t border-slate-700 mt-6 pt-4 text-center">
            <p className="text-slate-500 text-sm">© {new Date().getFullYear()} Record Kotha - Records haru ko ghar</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GamingPortfolio;